import { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Calendar,
  DollarSign,
  Target,
  BarChart3,
  Plus,
  Camera,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  AreaChart,
  Area,
} from 'recharts';
import api, { fetchApi } from '../api/client';

const PERIODS = ['1m', '3m', '6m', 'ytd', '1y', 'all'];

const formatCurrency = (val) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);

const formatPercent = (val) =>
  val != null ? `${val >= 0 ? '+' : ''}${(val * 100).toFixed(2)}%` : '—';

export default function Performance() {
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [realizedGains, setRealizedGains] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('ytd');
  const [showContributionForm, setShowContributionForm] = useState(false);
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  // Contribution form state
  const [newContribution, setNewContribution] = useState({
    account_id: 'fidelity_taxable',
    amount: '',
    source: '',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, historyRes, categoriesRes, contributionsRes, realizedGainsRes] = await Promise.all([
        fetchApi('/performance/summary'),
        fetchApi(`/performance/history?period=${period}`),
        fetchApi('/performance/categories'),
        fetchApi('/contributions'),
        fetchApi('/lots/realized-gains?source=complete'),  // Single source of truth
      ]);
      setSummary(summaryRes);
      setHistory(historyRes);
      setCategories(categoriesRes);
      setContributions(contributionsRes);
      setRealizedGains(realizedGainsRes);
    } catch (err) {
      console.error('Error fetching performance data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [period]);

  const handleTakeSnapshot = async () => {
    setSnapshotLoading(true);
    try {
      await fetchApi('/snapshots', { method: 'POST' });
      await fetchData();
    } catch (err) {
      console.error('Error taking snapshot:', err);
    } finally {
      setSnapshotLoading(false);
    }
  };

  const handleAddContribution = async (e) => {
    e.preventDefault();
    try {
      await fetchApi('/contributions', {
        method: 'POST',
        body: JSON.stringify({
          account_id: newContribution.account_id,
          amount: parseFloat(newContribution.amount),
          source: newContribution.source || undefined,
        }),
      });
      setNewContribution({ account_id: 'fidelity_taxable', amount: '', source: '' });
      setShowContributionForm(false);
      await fetchData();
    } catch (err) {
      console.error('Error adding contribution:', err);
    }
  };

  const handleDeleteContribution = async (id) => {
    try {
      await fetchApi(`/contributions/${id}`, { method: 'DELETE' });
      await fetchData();
    } catch (err) {
      console.error('Error deleting contribution:', err);
    }
  };

  // Prepare chart data
  const chartData = history?.dates?.map((date, i) => ({
    date,
    portfolio: history.portfolio_values[i],
    benchmark: history.benchmark_values[i],
  })) || [];

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="spinner w-8 h-8"></div>
          <p className="text-slate-400">Loading performance data...</p>
        </div>
      </div>
    );
  }

  // Calculate combined Total G/L (Unrealized + Realized)
  const unrealizedGL = summary?.total_gain_loss || 0;
  const realizedGL = realizedGains?.total_gain_loss || realizedGains?.total_realized_gain || 0;
  const totalGL = unrealizedGL + realizedGL;
  const isPositive = totalGL >= 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-100">Performance</h2>
          <p className="text-slate-400 mt-1">
            Portfolio returns and benchmark comparison
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleTakeSnapshot}
            disabled={snapshotLoading}
            className="btn btn-secondary"
          >
            <Camera className={`w-4 h-4 ${snapshotLoading ? 'animate-pulse' : ''}`} />
            Take Snapshot
          </button>
          <button
            onClick={() => setShowContributionForm(!showContributionForm)}
            className="btn btn-primary"
          >
            <Plus className="w-4 h-4" />
            Log Contribution
          </button>
        </div>
      </div>

      {/* Contribution Form */}
      {showContributionForm && (
        <div className="card p-4">
          <h3 className="text-lg font-semibold text-slate-200 mb-4">Log Contribution / Withdrawal</h3>
          <form onSubmit={handleAddContribution} className="flex items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm text-slate-400 mb-1">Account</label>
              <select
                value={newContribution.account_id}
                onChange={(e) => setNewContribution({ ...newContribution, account_id: e.target.value })}
                className="input w-full"
              >
                <option value="fidelity_taxable">Fidelity Taxable</option>
                <option value="fidelity_brokeragelink">BrokerageLink (401k)</option>
                <option value="rollover_ira">Rollover IRA</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm text-slate-400 mb-1">Amount (+ deposit, - withdrawal)</label>
              <input
                type="number"
                step="0.01"
                value={newContribution.amount}
                onChange={(e) => setNewContribution({ ...newContribution, amount: e.target.value })}
                placeholder="2000"
                className="input w-full"
                required
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm text-slate-400 mb-1">Source (optional)</label>
              <input
                type="text"
                value={newContribution.source}
                onChange={(e) => setNewContribution({ ...newContribution, source: e.target.value })}
                placeholder="HYSA transfer"
                className="input w-full"
              />
            </div>
            <button type="submit" className="btn btn-primary">
              Add
            </button>
            <button type="button" onClick={() => setShowContributionForm(false)} className="btn btn-ghost">
              Cancel
            </button>
          </form>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Total Value */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-slate-400">Total Value</span>
            <DollarSign className="w-5 h-5 text-slate-500" />
          </div>
          <div className="text-3xl font-bold text-slate-100">
            {formatCurrency(summary?.total_value || 0)}
          </div>
          <div className="text-sm text-slate-400 mt-2">
            Cost Basis: {formatCurrency(summary?.total_cost_basis || 0)}
          </div>
        </div>

        {/* Total G/L (Unrealized + Realized) */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-slate-400">Total Gain/Loss</span>
            {isPositive ? <TrendingUp className="w-5 h-5 text-emerald-500" /> : <TrendingDown className="w-5 h-5 text-rose-500" />}
          </div>
          <div className={`text-3xl font-bold ${isPositive ? 'text-emerald-400' : 'text-rose-400'}`}>
            {totalGL >= 0 ? '+' : ''}{formatCurrency(totalGL)}
          </div>
          <div className="flex justify-between text-xs mt-2">
            <span className="text-slate-400">
              Unrealized: <span className={unrealizedGL >= 0 ? 'text-emerald-400' : 'text-rose-400'}>{unrealizedGL >= 0 ? '+' : ''}{formatCurrency(unrealizedGL)}</span>
            </span>
            <span className="text-slate-400">
              Realized: <span className={realizedGL >= 0 ? 'text-emerald-400' : 'text-rose-400'}>{realizedGL >= 0 ? '+' : ''}{formatCurrency(realizedGL)}</span>
            </span>
          </div>
        </div>

        {/* YTD Return */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-slate-400">YTD Return (TWR)</span>
            <BarChart3 className="w-5 h-5 text-slate-500" />
          </div>
          <div className={`text-3xl font-bold ${
            (summary?.returns?.ytd?.twr || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'
          }`}>
            {formatPercent(summary?.returns?.ytd?.twr)}
          </div>
          {summary?.benchmark?.spy_return_ytd != null && (
            <div className="text-sm text-slate-400 mt-2">
              SPY: {formatPercent(summary.benchmark.spy_return_ytd)}
            </div>
          )}
        </div>

        {/* Alpha */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-slate-400">Alpha (YTD)</span>
            <Target className="w-5 h-5 text-slate-500" />
          </div>
          <div className={`text-3xl font-bold ${
            (summary?.benchmark?.alpha_ytd || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'
          }`}>
            {formatPercent(summary?.benchmark?.alpha_ytd)}
          </div>
          <div className="text-sm text-slate-400 mt-2">
            vs SPY benchmark
          </div>
        </div>

        {/* Snapshots */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-slate-400">History</span>
            <Calendar className="w-5 h-5 text-slate-500" />
          </div>
          <div className="text-3xl font-bold text-slate-100">
            {summary?.snapshot_count || 0}
          </div>
          <div className="text-sm text-slate-400 mt-2">
            snapshots recorded
          </div>
        </div>
      </div>

      {/* SPY Benchmark Card */}
      {summary?.benchmark?.current_value > 0 && (
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-200">📊 SPY Benchmark Tracking</h3>
            <span className="text-sm text-slate-400">
              If you invested everything in SPY instead
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-sm text-slate-400 mb-1">Benchmark Value</div>
              <div className="text-2xl font-bold text-slate-100">
                {formatCurrency(summary.benchmark.current_value)}
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-sm text-slate-400 mb-1">Equivalent SPY Shares</div>
              <div className="text-2xl font-bold text-slate-100">
                {(summary.benchmark.shares || 0).toFixed(2)}
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-sm text-slate-400 mb-1">SPY YTD Return</div>
              <div className={`text-2xl font-bold ${
                (summary.benchmark.spy_return_ytd || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'
              }`}>
                {summary.benchmark.spy_return_ytd != null 
                  ? formatPercent(summary.benchmark.spy_return_ytd)
                  : '—'}
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-sm text-slate-400 mb-1">Your Alpha (vs SPY)</div>
              <div className={`text-2xl font-bold ${
                (summary.benchmark.alpha_ytd || summary.benchmark.alpha_inception || 0) >= 0 
                  ? 'text-emerald-400' : 'text-rose-400'
              }`}>
                {summary.benchmark.alpha_ytd != null 
                  ? formatPercent(summary.benchmark.alpha_ytd)
                  : summary.benchmark.alpha_inception != null
                    ? formatPercent(summary.benchmark.alpha_inception)
                    : '—'}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {summary.benchmark.alpha_ytd != null ? 'YTD' : 
                 summary.benchmark.alpha_inception != null ? 'Since Inception' : 'Need more snapshots'}
              </div>
            </div>
          </div>
          {summary.snapshot_count === 1 && (
            <div className="mt-4 p-3 bg-slate-700/30 rounded-lg text-sm text-slate-400">
              💡 <strong>Benchmark initialized.</strong> After more snapshots, you'll see how your portfolio performs vs. if you had just bought SPY.
            </div>
          )}
        </div>
      )}

      {/* Return Periods */}
      <div className="card p-5">
        <h3 className="text-lg font-semibold text-slate-200 mb-4">Return by Period</h3>
        <div className="grid grid-cols-4 gap-4">
          {['mtd', 'ytd', '1y', 'inception'].map((p) => {
            const returns = summary?.returns?.[p];
            const twr = returns?.twr;
            return (
              <div key={p} className="text-center p-4 bg-slate-800/50 rounded-lg">
                <div className="text-sm text-slate-400 uppercase mb-2">{p}</div>
                <div className={`text-xl font-bold ${
                  (twr || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'
                }`}>
                  {formatPercent(twr)}
                </div>
                {returns?.simple != null && (
                  <div className="text-xs text-slate-500 mt-1">
                    Simple: {formatPercent(returns.simple)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Performance Chart */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-200">Portfolio Value</h3>
          <div className="flex gap-1">
            {PERIODS.map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  period === p
                    ? 'bg-sky-600 text-white'
                    : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                }`}
              >
                {p.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="portfolioGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="date"
                tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                stroke="#64748b"
                fontSize={12}
              />
              <YAxis
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                domain={['auto', 'auto']}
                stroke="#64748b"
                fontSize={12}
              />
              <Tooltip
                formatter={(value) => [formatCurrency(value), '']}
                labelFormatter={(label) => new Date(label).toLocaleDateString()}
                contentStyle={{
                  backgroundColor: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '8px',
                }}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="portfolio"
                name="Portfolio"
                stroke="#0ea5e9"
                fill="url(#portfolioGradient)"
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="benchmark"
                name="SPY Benchmark"
                stroke="#64748b"
                strokeWidth={1}
                strokeDasharray="5 5"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex items-center justify-center text-slate-400">
            <p>Take snapshots to see performance history</p>
          </div>
        )}
      </div>

      {/* Category Performance */}
      {categories.length > 0 && (
        <div className="card p-5">
          <h3 className="text-lg font-semibold text-slate-200 mb-4">Performance by Category</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Category</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Cost Basis</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Mkt Value</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Gain/Loss</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Return</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Weight</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Target</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat) => {
                  const gainLoss = cat.gain_loss || 0;
                  const totalReturn = cat.total_return;
                  return (
                    <tr key={cat.category} className="border-b border-slate-700/50 hover:bg-slate-800/30">
                      <td className="py-3 px-4 text-slate-200 font-medium">{cat.category}</td>
                      <td className="py-3 px-4 text-right text-slate-400">{formatCurrency(cat.cost_basis)}</td>
                      <td className="py-3 px-4 text-right text-slate-300">{formatCurrency(cat.current_value)}</td>
                      <td className={`py-3 px-4 text-right font-medium ${
                        gainLoss >= 0 ? 'text-emerald-400' : 'text-rose-400'
                      }`}>
                        {gainLoss >= 0 ? '+' : ''}{formatCurrency(gainLoss)}
                      </td>
                      <td className={`py-3 px-4 text-right font-medium ${
                        totalReturn != null 
                          ? (totalReturn >= 0 ? 'text-emerald-400' : 'text-rose-400')
                          : 'text-slate-500'
                      }`}>
                        {totalReturn != null ? `${(totalReturn * 100).toFixed(1)}%` : '-'}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-300">{(cat.current_weight * 100).toFixed(1)}%</td>
                      <td className="py-3 px-4 text-right text-slate-400">{(cat.target_weight * 100).toFixed(1)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Realized Gains */}
      {realizedGains && (realizedGains.total_sales > 0 || realizedGains.by_ticker?.length > 0) && (
        <div className="card p-5">
          <h3 className="text-lg font-semibold text-slate-200 mb-4">
            {realizedGains.year} Realized Gains/Losses
          </h3>
          
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-sm text-slate-400 mb-1">Total Proceeds</div>
              <div className="text-lg font-semibold text-slate-200">
                {formatCurrency(realizedGains.total_proceeds)}
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-sm text-slate-400 mb-1">Cost Basis</div>
              <div className="text-lg font-semibold text-slate-200">
                {formatCurrency(realizedGains.total_cost_basis)}
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-sm text-slate-400 mb-1">Net Realized Gain</div>
              <div className={`text-lg font-semibold ${
                (realizedGains.total_gain_loss || realizedGains.total_realized_gain || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'
              }`}>
                {(realizedGains.total_gain_loss || realizedGains.total_realized_gain || 0) >= 0 ? '+' : ''}{formatCurrency(realizedGains.total_gain_loss || realizedGains.total_realized_gain || 0)}
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-sm text-slate-400 mb-1">Positions Closed</div>
              <div className="text-lg font-semibold text-slate-200">
                {realizedGains.total_sales || realizedGains.total_lots_closed || 0}
              </div>
            </div>
          </div>
          
          {/* Term Breakdown */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                <span className="text-sm font-medium text-slate-300">Short-Term</span>
              </div>
              <div className={`text-xl font-bold ${
                realizedGains.short_term_gain >= 0 ? 'text-emerald-400' : 'text-rose-400'
              }`}>
                {realizedGains.short_term_gain >= 0 ? '+' : ''}{formatCurrency(realizedGains.short_term_gain)}
              </div>
              <div className="text-xs text-slate-500 mt-1">Held &lt; 1 year (taxed as income)</div>
            </div>
            <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
                <span className="text-sm font-medium text-slate-300">Long-Term</span>
              </div>
              <div className={`text-xl font-bold ${
                realizedGains.long_term_gain >= 0 ? 'text-emerald-400' : 'text-rose-400'
              }`}>
                {realizedGains.long_term_gain >= 0 ? '+' : ''}{formatCurrency(realizedGains.long_term_gain)}
              </div>
              <div className="text-xs text-slate-500 mt-1">Held ≥ 1 year (lower tax rate)</div>
            </div>
          </div>
          
          {/* By Ticker Table */}
          {realizedGains.by_ticker?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-slate-400 mb-3">By Position</h4>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2 px-3 text-xs font-medium text-slate-400">Ticker</th>
                      <th className="text-right py-2 px-3 text-xs font-medium text-slate-400">Proceeds</th>
                      <th className="text-right py-2 px-3 text-xs font-medium text-slate-400">Cost Basis</th>
                      <th className="text-right py-2 px-3 text-xs font-medium text-slate-400">Gain/Loss</th>
                    </tr>
                  </thead>
                  <tbody>
                    {realizedGains.by_ticker.map((item) => {
                      const gain = item.gain_loss ?? item.realized_gain ?? 0;
                      return (
                        <tr key={item.ticker} className="border-b border-slate-700/50 hover:bg-slate-800/30">
                          <td className="py-2 px-3 text-slate-200 font-medium">{item.ticker}</td>
                          <td className="py-2 px-3 text-right text-slate-300">{formatCurrency(item.proceeds)}</td>
                          <td className="py-2 px-3 text-right text-slate-400">{formatCurrency(item.cost_basis)}</td>
                          <td className={`py-2 px-3 text-right font-medium ${
                            gain >= 0 ? 'text-emerald-400' : 'text-rose-400'
                          }`}>
                            {gain >= 0 ? '+' : ''}{formatCurrency(gain)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {(realizedGains.total_sales === 0 && realizedGains.total_lots_closed === 0) && (
            <div className="text-center py-8 text-slate-400">
              <p>No realized gains recorded yet for {realizedGains.year}.</p>
              <p className="text-sm mt-2">
                Import Closed Positions from Fidelity to track realized gains.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Recent Contributions */}
      {contributions.length > 0 && (
        <div className="card p-5">
          <h3 className="text-lg font-semibold text-slate-200 mb-4">Recent Contributions</h3>
          <div className="space-y-2">
            {contributions.slice(0, 10).map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-2 h-2 rounded-full ${c.amount >= 0 ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                  <div>
                    <span className="text-slate-200">{c.account_id}</span>
                    {c.source && <span className="text-slate-400 text-sm ml-2">({c.source})</span>}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`font-medium ${c.amount >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {c.amount >= 0 ? '+' : ''}{formatCurrency(c.amount)}
                  </span>
                  <span className="text-slate-400 text-sm">{c.contribution_date}</span>
                  <button
                    onClick={() => handleDeleteContribution(c.id)}
                    className="text-slate-500 hover:text-rose-400 text-sm"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

